// src/components/inventory/InventoryComponent.tsx

import React from "react";
import { useInventory } from "../../hooks/useInventory";
import { logApiError, getUserMessage } from "../../api/apiError";
import {ParameterDataType, SelectionType} from "../../types/Category";
import {
    Inventory,
    InventoryParameter,
    ItemLocation,
    Visibility,
} from "../../types/Inventory";
import { CategoryParameter } from "../../types/Category";
import { Unit } from "../../types/Unit";
import InventoryForm from "./InventoryForm";
import ParameterRequestPanel from "../categoryRequest/ParameterRequestPanel";
import InventoryParameterForm from "./InventoryParameterForm";
import CreateParametersForm from "./CreateParametersForm";
import ServiceAreaEditor from "./ServiceAreaEditor";
import ItemLocationEditor from "./ItemLocationEditor";
import type { CoverageArea } from "../../utils/coverage";

interface InventoryComponentProps {
    token: string;
    initialCategoryId?: string;
    initialCategoryName?: string;
    initialInventoryId?: string; // when editing
    categoryParameters?: CategoryParameter[];
    categoryHasServiceArea?: boolean;
    categoryHasItemLocation?: boolean;
    units?: Unit[];
    onDeleted?: (id: string) => void;
}

const InventoryComponent: React.FC<InventoryComponentProps> = ({
                                                                   token,
                                                                   initialCategoryId,
                                                                   initialCategoryName,
                                                                   categoryHasServiceArea,
                                                                   categoryHasItemLocation,
                                                                   initialInventoryId,
                                                                   categoryParameters = [],
                                                                   units = [],
                                                                   onDeleted,
                                                               }) => {
    const {
        inventory,
        setInventory,
        loading,
        error,
        clearError,
        createInventory,
        loadInventory,
        updateInventory,
        deleteInventory,
        addParameter,
        changeParameterValue,
        removeParameter,
        uploadHtml,
        removeHtml,
        uploadPhoto,
        deletePhoto,
        reorderPhotos,
        setServiceArea,
        setItemLocation,
        createInventoryWithParameters,
    } = useInventory({ token });

    const [isNew, setIsNew] = React.useState(true);
    const [justCreated, setJustCreated] = React.useState(false);
    // Parameter values filled in while creating (keyed by name); applied via
    // AddParameter patches right after the create call.
    const [pendingParams, setPendingParams] = React.useState<Record<string, InventoryParameter>>({});
    // Service area / item location picked while creating, applied the same way.
    const [pendingCoverage, setPendingCoverage] = React.useState<CoverageArea[]>([]);
    const [pendingLocation, setPendingLocation] = React.useState<ItemLocation | null>(null);
    // Error shown at the bottom create button (the whole-page submit).
    const [createError, setCreateError] = React.useState<string | null>(null);
    const [editingParameter, setEditingParameter] =
        React.useState<InventoryParameter | null>(null);
    const [isNewParameter, setIsNewParameter] = React.useState(true);
    const [parameterError, setParameterError] = React.useState<string | null>(null);
    const [paramSaveError, setParamSaveError] = React.useState<string | null>(null);

    // Decide between CREATE vs EDIT whenever the props change
    React.useEffect(() => {
        console.log("InventoryComponent useEffect", {
            initialInventoryId,
            initialCategoryId,
        });

        // nothing selected
        if (!initialInventoryId && !initialCategoryId) {
            setIsNew(true);
            setInventory(null);
            return;
        }

        // EDIT mode: inventory id is present
        if (initialInventoryId) {
            console.log("Entering EDIT mode for inventory", initialInventoryId);
            setIsNew(false);
            setInventory(null); // clear any old draft
            loadInventory(initialInventoryId).catch((e) =>
                logApiError(e)
            );
            return;
        }

        // CREATE mode: no inventory id, but we do have a category id
        if (initialCategoryId) {
            console.log("Entering CREATE mode for category", initialCategoryId);
            setIsNew(true);
            setPendingParams({});
            setPendingCoverage([]);
            setPendingLocation(null);
            setCreateError(null);
            const draft: Inventory = {
                categoryId: initialCategoryId,
                id: "",
                version: "0",
                inventoryName: "",
                notes: "",
                description: "",
                url: "",
                visibility: Visibility.PUBLIC,
                parameters: [],
            };
            setInventory(draft);
        }
    }, [initialInventoryId, initialCategoryId]);

    // ---------- Inventory field handling ----------

    const handleInventoryFieldChange = (field: keyof Inventory, value: any) => {
        if (!inventory) return;
        setInventory({
            ...inventory,
            [field]: value,
        });
    };

    const handleSaveInventoryField = async (field: keyof Inventory, value: any) => {
        await updateInventory({ [field]: value } as Partial<Inventory>);
    };

    const handleInventorySave = async () => {
        if (!inventory) return;
        clearError();

        if (isNew) {
            // CREATE (throws on failure — including a blank listing — so we
            // only switch to edit mode when it really was created), applying
            // any parameter values filled in on the create form.
            await createInventoryWithParameters({
                categoryId: inventory.categoryId,
                inventoryName: inventory.inventoryName,
                notes: inventory.notes,
                description: inventory.description,
                url: inventory.url,
                visibility: inventory.visibility,
            }, Object.values(pendingParams), pendingCoverage, pendingLocation);
            setPendingParams({});
            setPendingCoverage([]);
            setPendingLocation(null);
            setIsNew(false);
            setJustCreated(true);
        } else {
            // EDIT
            await updateInventory({
                inventoryName: inventory.inventoryName,
                notes: inventory.notes,
                description: inventory.description,
                url: inventory.url,
                visibility: inventory.visibility,
            });
        }
    };

    const handleInventoryDelete = async () => {
        if (!inventory || !inventory.id) return;
        await deleteInventory();
        if (onDeleted) {
            onDeleted(inventory.id);
        }
    };

    // ---------- Parameter handling ----------

    const existingParamNames = inventory?.parameters.map((p) => p.name) ?? [];
    const existingParamIds = new Set((inventory?.parameters ?? []).map((p) => p.id));
    // Exclude params already on the listing by BOTH name and id — the backend
    // enforces uniqueness by id, so a name-only filter can still let a duplicate
    // id through (e.g. after a stale refresh) and trigger a server error.
    const availableParametersForAdd = categoryParameters.filter(
        (meta) => !existingParamNames.includes(meta.name) && !existingParamIds.has(meta.id)
    );
    const canAddMoreParameters = availableParametersForAdd.length > 0;

    const startAddParameter = () => {
        if (!canAddMoreParameters) return;
        setIsNewParameter(true);
        setEditingParameter({
            id: 0,
            name: "",
            value: "",
            dataType: ParameterDataType.STRING,
        });
    };

    const startEditParameter = (p: InventoryParameter) => {
        setIsNewParameter(false);
        setParamSaveError(null);
        setEditingParameter({ ...p });
    };

    const handleParameterFieldChange = (
        field: keyof InventoryParameter,
        value: any
    ) => {
        setEditingParameter((prev) => {
            if (!prev) return prev;
            return { ...prev, [field]: value };
        });
    };

    const buildParameterPayload = (): InventoryParameter | null => {
        if (!editingParameter) return null;
        const meta = categoryParameters.find((p) => p.name === editingParameter.name);
        if (!meta) return null;
        return {
            id: meta.id,
            name: editingParameter.name,
            value: editingParameter.value,
            dataType: meta.dataType as ParameterDataType,
            ...(meta.dataType === ParameterDataType.SELECTABLE
                ? { selectionType: (meta as any).selectionType ?? SelectionType.SINGLE }
                : {}),
            // Numeric params carry the unit the value is stated in
            // (required for DOUBLE so the server can build the value map).
            ...(meta.dataType === ParameterDataType.DOUBLE || meta.dataType === ParameterDataType.INTEGER
                ? { units: editingParameter.units }
                : {}),
        };
    };

    // Used by InventoryParameterForm (ADD mode) — throws so the form shows the error inline
    const handleParameterAdd = async () => {
        const payload = buildParameterPayload();
        if (!payload) { alert("Please select a parameter from the list."); return; }
        // Guard: the backend rejects duplicate parameter ids/names. Catch it here
        // with a clear message instead of a raw server error.
        const already = (inventory?.parameters ?? []).some(
            (p) => p.id === payload.id || p.name === payload.name
        );
        if (already) {
            throw new Error("That parameter is already on this listing.");
        }
        await addParameter(payload);
        setEditingParameter(null);
    };

    // Used by inline edit rows — catches and shows error in the row
    const handleInlineParameterSave = async () => {
        setParamSaveError(null);
        const payload = buildParameterPayload();
        if (!payload) return;
        try {
            await changeParameterValue(payload);
            setEditingParameter(null);
        } catch (err) {
            logApiError(err);
            setParamSaveError(getUserMessage(err));
        }
    };

    const handleParameterDelete = async (name: string) => {
        setParameterError(null);
        try {
            await removeParameter(name);
        } catch (err) {
            logApiError(err);
            setParameterError(getUserMessage(err));
        }
    };

    const categoryNameToShow = initialCategoryName;

    // ---------- Early render states ----------

    if (!initialInventoryId && !initialCategoryId) {
        return (
            <div className="inventory-component">
                <h2>Create or Edit Listing</h2>
                <p>Please select a category or click Edit on a search result.</p>
            </div>
        );
    }

    if (initialInventoryId && !inventory) {
        return (
            <div className="inventory-component">
                <h2>Edit Listing</h2>
                {error ? (
                    <>
                        <div className="error-banner">
                            {error}
                            <button onClick={clearError}>x</button>
                        </div>
                        <p>Failed to load listing.</p>
                    </>
                ) : (
                    <p>Loading listing...</p>
                )}
            </div>
        );
    }

    if (!initialInventoryId && !inventory) {
        return (
            <div className="inventory-component">
                <h2>Create Listing</h2>
                <p>Initializing new listing...</p>
            </div>
        );
    }

    // ---------- Main render ----------

    return (
        <div className="inventory-component">
            <h2>{isNew ? "Create Listing" : "Edit Listing"}</h2>

            {justCreated && (
                <div
                    style={{
                        color: "var(--success, #2e7d32)",
                        background: "var(--accent-soft, #e6f4ea)",
                        borderRadius: "6px",
                        padding: "0.5rem 0.75rem",
                        marginBottom: "0.75rem",
                        fontWeight: 600,
                    }}
                >
                    ✓ Listing created. You can now add photos, parameters and more below.
                </div>
            )}

            <InventoryForm
                inventory={inventory!}
                selectedCategoryName={categoryNameToShow}
                isNew={isNew}
                loading={loading}
                onFieldChange={handleInventoryFieldChange}
                onSaveField={handleSaveInventoryField}
                onSave={handleInventorySave}
                onDelete={!isNew ? handleInventoryDelete : undefined}
                onUploadHtml={!isNew ? async (file) => { await uploadHtml(file); } : undefined}
                onRemoveHtml={!isNew ? async () => { await removeHtml(); } : undefined}
                onUploadPhoto={!isNew ? async (file) => { await uploadPhoto(file); } : undefined}
                onDeletePhoto={!isNew ? async (photoId) => { await deletePhoto(photoId); } : undefined}
                onReorderPhotos={!isNew ? async (ids) => { await reorderPhotos(ids); } : undefined}
                token={token}
                onSetServiceArea={!isNew ? async (coverage) => { await setServiceArea(coverage); } : undefined}
                categoryHasServiceArea={categoryHasServiceArea}
                onSetItemLocation={!isNew ? async (location) => { await setItemLocation(location); } : undefined}
                categoryHasItemLocation={categoryHasItemLocation}
                hideCreateButton={isNew}
            />

            {/* Service area on the create form (service categories): picked up
                front, applied right after the create call. The editor "saves"
                into local state until then. */}
            {isNew && inventory && categoryHasServiceArea && (
                <div className="parameters-section">
                    <h3>Service area</h3>
                    <ServiceAreaEditor
                        token={token}
                        coverage={pendingCoverage}
                        onSave={async (coverage) => setPendingCoverage(coverage)}
                        disabled={loading}
                    />
                </div>
            )}

            {/* Item location on the create form (fixed-location categories). */}
            {isNew && inventory && categoryHasItemLocation && (
                <div className="parameters-section">
                    <h3>Location</h3>
                    <ItemLocationEditor
                        token={token}
                        location={pendingLocation}
                        onSave={async (location) => setPendingLocation(location)}
                        disabled={loading}
                    />
                </div>
            )}

            {/* Parameters on the create form: filled in up front (like search)
                and applied right after the create call. */}
            {isNew && inventory && categoryParameters.length > 0 && (
                <div className="parameters-section">
                    <h3>Parameters</h3>
                    <CreateParametersForm
                        parameters={categoryParameters}
                        units={units}
                        values={pendingParams}
                        onChange={(name, value) =>
                            setPendingParams((prev) => {
                                const next = { ...prev };
                                if (value) next[name] = value; else delete next[name];
                                return next;
                            })
                        }
                        disabled={loading}
                    />
                </div>
            )}

            {/* Parameters only after inventory has an id */}
            {inventory && inventory.id && (
                <div className="parameters-section">
                    <h3>Parameters</h3>

                    {parameterError && (
                        <div className="error-message" style={{ marginBottom: "0.5rem" }}>{parameterError}</div>
                    )}

                    {!(editingParameter && isNewParameter) && (
                        <button
                            onClick={startAddParameter}
                            disabled={loading || !canAddMoreParameters || !!editingParameter}
                        >
                            + Add Parameter
                        </button>
                    )}
                    {!canAddMoreParameters && !editingParameter && (
                        <p style={{ color: "#666", marginTop: "0.25rem" }}>
                            All category parameters are already used.
                        </p>
                    )}

                    {inventory.parameters.length > 0 && (
                        <div className="parameter-grid-header">
                            <span>Parameter</span>
                            <span>Value</span>
                        </div>
                    )}

                    <ul className="parameter-list">
                        {inventory.parameters.map((p) => {
                            const isInlineEditing =
                                !isNewParameter && editingParameter?.name === p.name;
                            const meta = categoryParameters.find((m) => m.name === p.name);
                            const selectionType =
                                (meta as any)?.selectionType ?? SelectionType.SINGLE;
                            const options: string[] =
                                (meta as any)?.options ?? [];
                            const isNumeric = meta?.dataType === ParameterDataType.INTEGER || meta?.dataType === ParameterDataType.DOUBLE;
                            // Prefer the item's own stated unit; fall back to the category unit.
                            const listUnitId = isNumeric ? (p.units ?? meta?.unitId) : undefined;
                            const unitLabel = listUnitId
                                ? (units.find(u => u.id === listUnitId)?.name ?? listUnitId)
                                : null;

                            return (
                                <li key={p.name} className="parameter-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                                    <div className="parameter-row-main">
                                        <span className="parameter-name">{p.name}</span>
                                        {!isInlineEditing && (
                                            <>
                                                <span className="parameter-value">
                                                    {Array.isArray(p.value)
                                                        ? p.value.join(", ")
                                                        : String(p.value ?? "")}
                                                    {unitLabel && (
                                                        <span style={{ marginLeft: "0.35rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                                                            {unitLabel}
                                                        </span>
                                                    )}
                                                </span>
                                                <div className="parameter-row-actions" style={{ marginLeft: "auto" }}>
                                                    <button
                                                        onClick={() => startEditParameter(p)}
                                                        disabled={loading || !!editingParameter}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleParameterDelete(p.name)}
                                                        disabled={loading || !!editingParameter}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {isInlineEditing && (
                                        <div style={{ marginTop: "0.5rem", width: "100%" }}>
                                            {meta?.dataType === ParameterDataType.BOOLEAN ? (
                                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            typeof editingParameter!.value === "boolean"
                                                                ? editingParameter!.value
                                                                : editingParameter!.value === "true"
                                                        }
                                                        onChange={(e) =>
                                                            handleParameterFieldChange("value", e.target.checked)
                                                        }
                                                        disabled={loading}
                                                    />
                                                    {editingParameter!.value ? "true" : "false"}
                                                </label>
                                            ) : meta?.dataType === ParameterDataType.SELECTABLE &&
                                              selectionType === SelectionType.SINGLE ? (
                                                <select
                                                    value={editingParameter!.value ?? ""}
                                                    onChange={(e) =>
                                                        handleParameterFieldChange("value", e.target.value)
                                                    }
                                                    disabled={loading}
                                                >
                                                    <option value="">-- select --</option>
                                                    {options.map((opt) => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            ) : meta?.dataType === ParameterDataType.SELECTABLE ? (
                                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                    {options.map((opt) => {
                                                        const selected: string[] = Array.isArray(editingParameter!.value)
                                                            ? editingParameter!.value
                                                            : [];
                                                        return (
                                                            <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selected.includes(opt)}
                                                                    onChange={(e) => {
                                                                        const next = e.target.checked
                                                                            ? [...selected, opt]
                                                                            : selected.filter((v) => v !== opt);
                                                                        handleParameterFieldChange("value", next);
                                                                    }}
                                                                    disabled={loading}
                                                                />
                                                                {opt}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <input
                                                        type="text"
                                                        value={editingParameter!.value ?? ""}
                                                        onChange={(e) =>
                                                            handleParameterFieldChange("value", e.target.value)
                                                        }
                                                        disabled={loading}
                                                    />
                                                    {unitLabel && (
                                                        <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                                                            {unitLabel}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                                                <button onClick={handleInlineParameterSave} disabled={loading}>
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => { setEditingParameter(null); setParamSaveError(null); }}
                                                    disabled={loading}
                                                    className="btn-secondary"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            {paramSaveError && (
                                                <div className="error-message" style={{ marginTop: "0.25rem" }}>{paramSaveError}</div>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {editingParameter && isNewParameter && (
                        <InventoryParameterForm
                            parameter={editingParameter}
                            availableParameters={availableParametersForAdd}
                            units={units}
                            isNew={true}
                            onFieldChange={handleParameterFieldChange}
                            onSave={handleParameterAdd}
                            onCancel={() => setEditingParameter(null)}
                            loading={loading}
                        />
                    )}

                    {/* Ask for a parameter this category is missing. */}
                    <ParameterRequestPanel
                        token={token}
                        categoryId={inventory.categoryId}
                        categoryName={categoryNameToShow}
                    />
                </div>
            )}

            {/* Create mode: ONE submit for the whole page (details + service
                area / location + parameters), pinned so it stays visible while
                scrolling long parameter lists. */}
            {isNew && inventory && (
                <div style={{ position: "sticky", bottom: 0, background: "var(--bg-primary, #fff)", borderTop: "1px solid var(--border-color, #ddd)", padding: "0.75rem 0", marginTop: "1rem", zIndex: 5 }}>
                    <button
                        onClick={async () => {
                            setCreateError(null);
                            try {
                                await handleInventorySave();
                            } catch (err) {
                                logApiError(err);
                                setCreateError(getUserMessage(err));
                            }
                        }}
                        disabled={loading}
                    >
                        {loading ? "Creating…" : "Create listing"}
                    </button>
                    <span style={{ marginLeft: "0.75rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                        Saves everything above — details{categoryHasServiceArea ? ", service area" : ""}{categoryHasItemLocation ? ", location" : ""}{categoryParameters.length > 0 ? ", and parameters" : ""}.
                    </span>
                    {createError && (
                        <div className="error-message" style={{ marginTop: "0.5rem" }}>{createError}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InventoryComponent;